/*!
 * Adapted from "The Furrow - ASCIIxGPT - Concept 5" by The Furrow
 * (https://thefurrow.tv/project/asciixgpt). Changes: ported to a WebGL2 shader,
 * with a different pattern set, glyphs, colors, settings, and a carve around content.
 * This file is licensed under CC BY-NC-SA 4.0:
 * https://creativecommons.org/licenses/by-nc-sa/4.0/
 */

// The carve and its breakouts (the areas the field keeps clear), measured from the page in
// viewport CSS pixels. The boxes update in place, so the frame loop doesn't allocate here.
// They only move on a scroll or a layout change, so the scroll event and the resize observers
// measure them, not the frame: a frame can come after another part's style writes, and
// reading layout there forces a reflow.

import type { Box } from "@bfly/engine";
import { bandWidth, capEnds, type CarveShape } from "./carve.js";

const emptyBox = (): Box => ({ left: 0, top: 0, right: 0, bottom: 0 });
// The dithered band's width in rows, if the carve doesn't set `--edge-rows`.
const EDGE_ROWS = 11;

export class CarveMeasure {
  /** The column, the top and tip of its egg-shaped end, and the padded breakouts. */
  readonly shape: CarveShape & { column: Box; breakouts: readonly Box[] };
  /** The dithered band's width in CSS pixels. */
  band = 1;
  /** The dithered band's maximum width in rows: the carve's `--edge-rows`, which the page
      sets per breakpoint (`readStyles()` reads it). */
  edgeRows = EDGE_ROWS;
  // The taper element's `--cap-height` and `--cap-drop` (null means no egg end), and the
  // carve's inline padding, which pads each breakout too. `readStyles()` fills both.
  private cap: { height: number; drop: number } | null = null;
  private pad = 0;

  /** `taper`: below its top, the carve narrows like the end of an egg. `breakouts`: content
      wider than the column, which the field keeps clear too. */
  constructor(
    private readonly carve: HTMLElement,
    private readonly taper: HTMLElement | null,
    private readonly breakouts: readonly HTMLElement[],
  ) {
    this.shape = {
      column: emptyBox(),
      capTop: 0,
      capBottom: 0,
      breakouts: breakouts.map(emptyBox),
    };
  }

  /** Call after a layout change. Reads the egg end's shape, the padding, and the band's rows,
      which change at breakpoints. */
  readStyles(): void {
    if (this.taper) {
      const style = getComputedStyle(this.taper);
      const height = Number.parseFloat(style.getPropertyValue("--cap-height"));
      const drop = Number.parseFloat(style.getPropertyValue("--cap-drop"));
      this.cap = height > 0 ? { height, drop: drop || 0 } : null;
    }
    const carve = getComputedStyle(this.carve);
    this.pad = Number.parseFloat(carve.paddingLeft) || 0;
    this.edgeRows = Number.parseFloat(carve.getPropertyValue("--edge-rows")) || EDGE_ROWS;
  }

  /** Measures the boxes now, and the band for a viewport `width` wide, between `least` and
      `widest` CSS pixels. */
  measure(width: number, widest: number, least: number): void {
    const rect = this.carve.getBoundingClientRect();
    const { shape, pad } = this;
    const { column } = shape;
    column.left = rect.left;
    column.top = rect.top;
    column.right = rect.right;
    column.bottom = rect.bottom;
    const cap = capEnds(column, this.cap);
    shape.capTop = cap.top;
    shape.capBottom = cap.bottom;
    for (let i = 0; i < this.breakouts.length; i++) {
      const box = shape.breakouts[i];
      const element = this.breakouts[i];
      if (!box || !element) continue;
      const r = element.getBoundingClientRect();
      box.left = r.left - pad;
      box.top = r.top - pad;
      box.right = r.right + pad;
      box.bottom = r.bottom + pad;
    }
    this.band = bandWidth(shape, width, widest, least);
  }
}
